import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCounterData } from '../../hooks/useCounterData';
import { useSortedStats } from '../../hooks/useSortedStats';
import { useDateFormatters } from '../../hooks/useDateFormatters';
import { useOwnerUrl } from '../../hooks/useOwnerUrl';
import StatsTable from '../common/StatsTable';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../utils/toastUtils';
import { useCounters } from '../../contexts/CounterContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { updateCounterMetaTags, resetMetaTags } from '../../utils/metaTagsUtils';
import { useUser } from '../../contexts/UserContext';
import { isOwnerUrlIdentifier, findCounterByIdentifier } from '../../utils/urlUtils';

/**
 * Statistics component displays the statistics table for a specific counter.
 * 
 * This component is responsible for:
 * - Showing statistics for button clicks
 * - Providing sorting functionality
 * - Displaying statistics in a table format
 * 
 * The component inherits the same access rights as the Counter component.
 */
function Statistics() {
  const { t } = useTranslation();
  const { counterId } = useParams<{ counterId?: string }>();
  const { toasts } = useToast();
  const { counters, loading: countersLoading } = useCounters();
  const { hasCounterAccess, user } = useUser();
  const [counterNotFound, setCounterNotFound] = useState(false);
  
  // Use the owner URL hook to determine if this is an owner URL access
  const { isOwnerUrl, counterIdentifier } = useOwnerUrl(counterId);
  
  // Fetch counter data
  const { 
    stats, 
    loading, 
    error,
    refreshing,
    lastUpdate
  } = useCounterData(counterIdentifier || '', isOwnerUrl);
  
  // Setup sorting
  const { sortField, sortDirection, handleSort, getSortedStats } = useSortedStats(stats);
  
  // Setup date formatters
  const { formatLastClick } = useDateFormatters();
  
  // Get the counter name to use in the document title
  const currentCounter = counterIdentifier ? 
    counters.find(c => isOwnerUrl ? c.ownerUrl === counterIdentifier : c._id === counterIdentifier) : 
    undefined;
  
  // Set document title
  useDocumentTitle(
    currentCounter?.name,
    'TapDonkey',
    currentCounter?.name ? `${currentCounter.name} - Track and analyze clicks with TapDonkey` : undefined
  );

  // Update meta tags for social sharing when counter data changes
  useEffect(() => {
    if (currentCounter) {
      // Update meta tags with dynamic counter information for statistics page
      updateCounterMetaTags(currentCounter);
    }
    
    // Reset meta tags when component unmounts
    return () => {
      resetMetaTags();
    };
  }, [currentCounter]);

  // Reset counterNotFound when counters finish loading to prevent premature redirects
  useEffect(() => {
    if (!countersLoading) {
      // When counters finish loading, check if the counter exists
      const foundCounter = findCounterByIdentifier(counterId || '', counters);
      // Only set counterNotFound if we're sure counters are loaded and counter wasn't found
      setCounterNotFound(!foundCounter && !!counterId);
    }
  }, [countersLoading, counterId, counters]);

  // If no counterId is found, redirect to counters list
  if (!counterId) {
    return <Navigate to="/counters" replace />;
  }
  
  // Don't make any redirect decisions while counters are still loading
  if (countersLoading) {
    return <LoadingSpinner size="lg" />;
  }
  
  // Determine if this is an owner URL using our consistent utility
  const isOwnerUrlAccess = isOwnerUrlIdentifier(counterId, counters);
  
  // If counter not found, redirect to not found page
  if (counterNotFound) {
    return <Navigate to="/counter-not-found" replace />;
  }
  
  // Get counter details directly to ensure we have the latest data
  const counterDetails = findCounterByIdentifier(counterId, counters);
  
  // Double-check we have counter details before checking access
  // Only redirect if we're sure counters have loaded
  if (!counterDetails && !countersLoading) {
    return <Navigate to="/counter-not-found" replace />;
  }
  
  // Don't attempt access control checks if we don't have counter details yet
  if (!counterDetails) {
    return <LoadingSpinner size="lg" />;
  }
  
  // Use our centralized access control with direct counter reference
  const hasViewAccess = hasCounterAccess(counterDetails.ownerEmail || null, isOwnerUrlAccess, 'view');
  
  // Redirect if no view access
  if (!hasViewAccess) {
    // For owner URLs of logged-in user counters, show "not found" page if denied access for public users
    if (isOwnerUrlAccess && counterDetails.ownerEmail !== null && user.email === null) {
      return <Navigate to="/counter-not-found" replace />;
    }
    
    // For other denied access cases, redirect to counters list
    return <Navigate to="/counters" replace />;
  }

  if (loading && !stats.length) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div className="container py-3 py-md-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="text-center mb-3 mb-md-4">
            <h1 className="h3 h1-md fw-bold mb-0">{t('statistics.title')}</h1>
          </div>
          
          <ErrorAlert message={error} />

          {refreshing && (
            <div className="loading-indicator">
              <div className="spinner-border spinner-border-sm text-secondary spinner-sm" role="status">
                <span className="visually-hidden">{t('common.refreshing')}</span>
              </div>
            </div>
          )}

          <div className="mb-4">
            <StatsTable 
              stats={getSortedStats()} 
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              formatLastClick={formatLastClick}
            />
          </div>
        </div>
      </div>
      
      <ToastContainer toasts={toasts} />
    </div>
  );
}

export default Statistics; 